import { useEffect, useState } from "react";

import AssignmentApi from "@/api/assignment-api";
import CompetencyApi from "@/api/competency";
import Button from "@/components/common/input/button";
import { logRejection } from "@/helpers";
import type { Competency } from "@/types/competency";

// toggleFn arrives from the surrounding Modal through cloneElement, never from the call site.
interface CompetencyOverrideViewerProps {
  competency_id: string;
  assignment_id?: string;
  toggleFn?: () => void;
}

export default function CompetencyOverrideViewer({
  competency_id,
  assignment_id,
  toggleFn,
}: CompetencyOverrideViewerProps) {
  const [state, setState] = useState<Competency | null>(null);

  const _handleCancel = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    toggleFn?.();
  };

  useEffect(() => {
    const abort_controller = new AbortController();

    const fetchData = async () => {
      const base_data = await CompetencyApi.fetchOne(competency_id, abort_controller.signal);
      if (!base_data.data) {
        return;
      }
      let override: Competency | null = null;
      if (assignment_id) {
        const response = await AssignmentApi.getAssignmentCompetency(
          assignment_id,
          competency_id,
          abort_controller.signal,
        );
        override = response.data;
      }
      const combined_data: Competency = {
        ...base_data.data,
      };
      // check for overrides
      if (override) {
        if (override.description !== null) {
          combined_data.description = override.description;
        }
        if (override.questions_leader !== null) {
          combined_data.questions_leader = override.questions_leader;
        }
        if (override.questions_ic !== null) {
          combined_data.questions_ic = override.questions_ic;
        }
        if (override.positive_indicators !== null) {
          combined_data.positive_indicators = override.positive_indicators;
        }
        if (override.negative_indicators !== null) {
          combined_data.negative_indicators = override.negative_indicators;
        }
      }
      setState(combined_data);
    };
    fetchData().catch(logRejection);
    return () => {
      abort_controller.abort();
    };
  }, [competency_id, assignment_id]);

  if (!state) {
    return null;
  }

  return (
    <div className="mt-4 w-[60rem] rounded-lg bg-slate-800 p-8">
      <div key={state.id}>
        <h2 className="mb-2 inline-block bg-slate-800 px-4 text-2xl font-bold">{state.name}</h2>
        <div className="flex items-center justify-between rounded-tr-md bg-slate-800 p-4">
          <p className="max-w-[40rem] text-slate-300">{state.description}</p>
        </div>
        <div className="space-y-8 rounded-t-md bg-slate-900 px-4 py-8">
          <p className="font-bold text-slate-100">Questions</p>
          {state.questions_leader.map((q, idx) => {
            return (
              <p key={idx} className="max-w-[40rem]">
                {q}
              </p>
            );
          })}
        </div>
        <div className="grid grid-cols-2">
          <div className="rounded-bl-md bg-green-200 px-4 pt-4 pb-2">
            <p className="pb-2 font-bold text-slate-600">Positive indicators</p>
            <ul className="ml-4 list-disc text-neutral-900">
              {state.positive_indicators.map((pi, idx) => {
                return (
                  <li key={idx} className="mb-2">
                    {pi}
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="rounded-br-md bg-red-200 px-4 pt-4 pb-2">
            <p className="pb-2 font-bold text-slate-600">Negative indicators</p>
            <ul className="ml-4 list-disc text-neutral-900">
              {state.negative_indicators.map((ni, idx) => {
                return (
                  <li key={idx} className="mb-2">
                    {ni}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <Button onClick={_handleCancel}>Close</Button>
      </div>
    </div>
  );
}
